import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { message } from 'antd';
import {
  listLlmOverridesAction,
  updateLlmOverridesAction,
} from 'src/modules/SuperAdmin/store/actions/promptsv2.action';
import {
  setTabConfigurationSlice,
  updateTabConfigurationSlice,
  setTabUnsavedChangesSlice,
  setTabSelectionSlice,
  resetTabConfigurationSlice,
} from 'src/modules/SuperAdmin/store/slices/prompts.slice';
import { getFromStorage } from 'src/lib/storage';
import organizationEventEmitter from 'src/utilities/organizationEmmiter';

// Constants for display names
const DISPLAY_NAMES = {
  org: 'Organization',
  practitioner: 'Practitioner',
  plan: 'Plan',
};

export const useLLMOverrides = () => {
  const dispatch = useDispatch();
  const {
    llms,
    llmsLoading,
    llmOverrides,
    llmOverridesLoading,
    tabConfigurations,
    tabUnsavedChanges,
    tabSelections,
  } = useSelector((state) => state.promptsState);

  const [selectedOrganization, setSelectedOrganization] = useState(() => {
    const savedOrg = getFromStorage('superAdminSelectedOrganization');
    return savedOrg ? JSON.parse(savedOrg) : null;
  });

  // Load LLM overrides when organization changes
  useEffect(() => {
    if (selectedOrganization?.org_uuid) {
      dispatch(listLlmOverridesAction(selectedOrganization.org_uuid));
    }
  }, [dispatch, selectedOrganization?.org_uuid]);

  // Listen for organization changes
  useEffect(() => {
    const unsubscribe = organizationEventEmitter.subscribe(
      'organizationChanged',
      setSelectedOrganization
    );
    return unsubscribe;
  }, []);

  // Helper functions

  // Helper function to extract LLM models from configurations
  const extractLLMModels = (configurations, predefinedUseCases) => {
    const modelsInUse = new Set();

    for (const [useCaseKey, useCase] of Object.entries(predefinedUseCases)) {
      for (const [settingKey, setting] of Object.entries(useCase.settings)) {
        for (const option of setting.options) {
          const optionKey = option.toLowerCase();
          const llmModel =
            configurations[useCaseKey]?.[settingKey]?.[optionKey]?.llm;

          if (llmModel) {
            modelsInUse.add(llmModel);
          }
        }
      }
    }

    return modelsInUse;
  };

  // Helper function to get all prompt screens
  const getAllPromptScreens = (predefinedUseCases) => {
    const screens = new Set();

    for (const useCase of Object.values(predefinedUseCases)) {
      for (const setting of Object.values(useCase.settings)) {
        for (const [optionKey, promptScreens] of Object.entries(
          setting.promptScreenMapping || {}
        )) {
          promptScreens.forEach((screen) => screens.add(screen));
        }
      }
    }

    return screens;
  };

  // Helper function to create override entry for an LLM model
  const createOverrideEntry = (
    llmModel,
    configurations,
    predefinedUseCases,
    levelKey,
    levelValue
  ) => {
    const llmData = llms?.llms?.find((llm) => llm.model === llmModel);
    if (!llmData) return null;

    const allScreens = getAllPromptScreens(predefinedUseCases);

    // Initialize override entry
    const overrideEntry = {
      llm_id: llmData.llm_id,
      plan_types: {},
      orgs: {},
      practitioners: {},
    };

    // Initialize empty arrays for all screens
    allScreens.forEach((screen) => {
      overrideEntry.plan_types[screen] = [];
      overrideEntry.orgs[screen] = [];
      overrideEntry.practitioners[screen] = [];
    });

    // Populate screens for this LLM model at the specified level
    for (const [useCaseKey, useCase] of Object.entries(predefinedUseCases)) {
      for (const [settingKey, setting] of Object.entries(useCase.settings)) {
        for (const option of setting.options) {
          const optionKey = option.toLowerCase();
          const configLlm =
            configurations[useCaseKey]?.[settingKey]?.[optionKey]?.llm;

          if (configLlm === llmModel) {
            const promptScreens =
              setting.promptScreenMapping?.[optionKey] || [];
            promptScreens.forEach((screen) => {
              if (
                levelValue &&
                !overrideEntry[levelKey][screen].includes(levelValue)
              ) {
                overrideEntry[levelKey][screen].push(levelValue);
              }
            });
          }
        }
      }
    }

    return overrideEntry;
  };

  // Helper function to process LLM overrides
  const processLLMOverrides = async (
    configurations,
    predefinedUseCases,
    levelKey,
    levelValue,
    displayName
  ) => {
    const modelsInUse = extractLLMModels(configurations, predefinedUseCases);
    const payload = [];
    const processedLlmIds = new Set();

    // Process models in use - either update existing overrides or create new ones
    modelsInUse.forEach((llmModel) => {
      const llmData = llms?.llms?.find((llm) => llm.model === llmModel);
      if (!llmData) return;

      // Check if there's an existing override for this LLM
      const existingOverride = llmOverrides?.find(
        (override) => override.llm_id === llmData.llm_id
      );

      if (existingOverride) {
        // Update existing override - preserve all levels with deep copy of arrays
        const updatedOverride = {
          ...existingOverride,
          orgs: {},
          practitioners: {},
          plan_types: {},
        };

        // Deep copy all arrays for each level
        for (const [screen, arr] of Object.entries(
          existingOverride.orgs || {}
        )) {
          updatedOverride.orgs[screen] = [...arr];
        }
        for (const [screen, arr] of Object.entries(
          existingOverride.practitioners || {}
        )) {
          updatedOverride.practitioners[screen] = [...arr];
        }
        for (const [screen, arr] of Object.entries(
          existingOverride.plan_types || {}
        )) {
          updatedOverride.plan_types[screen] = [...arr];
        }

        // Clear existing entries for this level value first
        for (const [screen, levelArray] of Object.entries(
          updatedOverride[levelKey]
        )) {
          updatedOverride[levelKey][screen] = levelArray.filter(
            (value) => value !== levelValue
          );
        }

        // Add new entries based on current configuration
        for (const [useCaseKey, useCase] of Object.entries(
          predefinedUseCases
        )) {
          for (const [settingKey, setting] of Object.entries(
            useCase.settings
          )) {
            for (const option of setting.options) {
              const optionKey = option.toLowerCase();
              const configLlm =
                configurations[useCaseKey]?.[settingKey]?.[optionKey]?.llm;

              if (configLlm === llmModel) {
                const promptScreens =
                  setting.promptScreenMapping?.[optionKey] || [];
                promptScreens.forEach((screen) => {
                  // Initialize array if it doesn't exist for this screen
                  if (!updatedOverride[levelKey][screen]) {
                    updatedOverride[levelKey][screen] = [];
                  }
                  if (
                    levelValue &&
                    !updatedOverride[levelKey][screen].includes(levelValue)
                  ) {
                    updatedOverride[levelKey][screen].push(levelValue);
                  }
                });
              }
            }
          }
        }

        payload.push(updatedOverride);
      } else {
        // Create new override entry
        const overrideEntry = createOverrideEntry(
          llmModel,
          configurations,
          predefinedUseCases,
          levelKey,
          levelValue
        );
        if (overrideEntry) {
          payload.push(overrideEntry);
        }
      }

      processedLlmIds.add(llmData.llm_id);
    });

    // Remove overrides for models no longer in use
    if (llmOverrides?.length) {
      llmOverrides.forEach((existingOverride) => {
        // Skip if we already processed this LLM
        if (processedLlmIds.has(existingOverride.llm_id)) return;

        const updatedOverride = {
          ...existingOverride,
          orgs: {},
          practitioners: {},
          plan_types: {},
        };

        // Deep copy all arrays for each level
        for (const [screen, arr] of Object.entries(
          existingOverride.orgs || {}
        )) {
          updatedOverride.orgs[screen] = [...arr];
        }
        for (const [screen, arr] of Object.entries(
          existingOverride.practitioners || {}
        )) {
          updatedOverride.practitioners[screen] = [...arr];
        }
        for (const [screen, arr] of Object.entries(
          existingOverride.plan_types || {}
        )) {
          updatedOverride.plan_types[screen] = [...arr];
        }

        let hasChanges = false;
        for (const [screen, levelArray] of Object.entries(
          updatedOverride[levelKey]
        )) {
          if (levelArray.includes(levelValue)) {
            updatedOverride[levelKey][screen] = levelArray.filter(
              (value) => value !== levelValue
            );
            hasChanges = true;
          }
        }

        // Always include the override if it has data in any level, even if we made changes to the current level
        if (hasChanges) {
          payload.push(updatedOverride);
        } else {
          // Even if no changes were made to this level, we should still include the override
          // if it has data in other levels to preserve the complete structure
          const hasOrgs = Object.values(updatedOverride.orgs || {}).some(
            (arr) => arr.length > 0
          );
          const hasPractitioners = Object.values(
            updatedOverride.practitioners || {}
          ).some((arr) => arr.length > 0);
          const hasPlanTypes = Object.values(
            updatedOverride.plan_types || {}
          ).some((arr) => arr.length > 0);

          if (hasOrgs || hasPractitioners || hasPlanTypes) {
            payload.push(updatedOverride);
          }
        }
      });
    }

    // Only filter out entries where ALL level arrays are completely empty
    // This ensures we preserve entries that have data in other levels
    const filteredPayload = payload.filter((entry) => {
      const hasOrgs = Object.values(entry.orgs || {}).some(
        (arr) => arr.length > 0
      );
      const hasPractitioners = Object.values(entry.practitioners || {}).some(
        (arr) => arr.length > 0
      );
      const hasPlanTypes = Object.values(entry.plan_types || {}).some(
        (arr) => arr.length > 0
      );

      // Keep the entry if ANY level has data
      return hasOrgs || hasPractitioners || hasPlanTypes;
    });

    await dispatch(
      updateLlmOverridesAction(selectedOrganization.org_uuid, filteredPayload)
    );
    message.success(`${displayName} LLM overrides saved successfully`);
    return true;
  };

  // Helper function to load configurations from overrides
  const loadConfigurationsHelper = (
    predefinedUseCases,
    levelKey,
    levelValue
  ) => {
    const configurations = {};

    // Initialize configurations with default values
    for (const [useCaseKey, useCase] of Object.entries(predefinedUseCases)) {
      configurations[useCaseKey] = {};

      for (const [settingKey, setting] of Object.entries(useCase.settings)) {
        configurations[useCaseKey][settingKey] = {};

        for (const option of setting.options) {
          const optionKey = option.toLowerCase();
          configurations[useCaseKey][settingKey][optionKey] = { llm: null };
        }
      }
    }

    // Apply overrides
    for (const [useCaseKey, useCase] of Object.entries(predefinedUseCases)) {
      for (const [settingKey, setting] of Object.entries(useCase.settings)) {
        for (const option of setting.options) {
          const optionKey = option.toLowerCase();
          const promptScreens = setting.promptScreenMapping?.[optionKey] || [];

          // Find override for any of the prompt screens
          for (const promptScreen of promptScreens) {
            const override = llmOverrides.find((override) =>
              override[levelKey]?.[promptScreen]?.includes(levelValue)
            );

            if (override) {
              const llmModel = llms?.llms?.find(
                (llm) => llm.llm_id === override.llm_id
              );
              if (llmModel) {
                configurations[useCaseKey][settingKey][optionKey].llm =
                  llmModel.model;
                break; // Use the first override found
              }
            }
          }
        }
      }
    }

    return configurations;
  };

  // Initialize tab configuration from overrides
  const initializeTabConfiguration = useCallback(
    (tab, predefinedUseCases, selectedPractitioners = []) => {
      // Only initialize if we have the necessary data
      if (!llmOverrides || !llms?.llms) {
        return;
      }

      let configurations;
      if (tab === 'org') {
        configurations = loadConfigurationsHelper(
          predefinedUseCases,
          'orgs',
          selectedOrganization?.org_uuid
        );
      } else if (tab === 'practitioner') {
        const practitionerId =
          selectedPractitioners.length > 0 ? selectedPractitioners[0] : null;
        configurations = loadConfigurationsHelper(
          predefinedUseCases,
          'practitioners',
          practitionerId
        );
      } else if (tab === 'plan') {
        const planType =
          selectedPractitioners.length > 0 ? selectedPractitioners[0] : null;
        configurations = loadConfigurationsHelper(
          predefinedUseCases,
          'plan_types',
          planType
        );
      }

      dispatch(setTabConfigurationSlice({ tab, configurations }));
      dispatch(setTabUnsavedChangesSlice({ tab, hasChanges: false }));
    },
    [dispatch, llmOverrides, llms]
  );

  // Update tab configuration
  const updateTabConfiguration = useCallback(
    (tab, useCaseKey, settingKey, optionKey, field, value) => {
      dispatch(
        updateTabConfigurationSlice({
          tab,
          useCaseKey,
          settingKey,
          optionKey,
          field,
          value,
        })
      );
      dispatch(setTabUnsavedChangesSlice({ tab, hasChanges: true }));
    },
    [dispatch]
  );

  // Set tab selection (practitioner ID or plan type)
  const setTabSelection = useCallback(
    (tab, selection) => {
      dispatch(setTabSelectionSlice({ tab, selection }));
    },
    [dispatch]
  );

  // Get tab configuration
  const getTabConfiguration = (tab) => {
    return tabConfigurations[tab] || {};
  };

  // Get tab unsaved changes status
  const getTabUnsavedChanges = (tab) => {
    return tabUnsavedChanges[tab] || false;
  };

  // Get tab selection
  const getTabSelection = (tab) => {
    return tabSelections[tab];
  };

  // Reset tab configuration
  const resetTabConfiguration = useCallback(
    (tab) => {
      dispatch(resetTabConfigurationSlice({ tab }));
    },
    [dispatch]
  );

  // Save tab configurations (updated to use Redux state)
  const saveTabConfigurations = useCallback(
    async (tab, predefinedUseCases, selectedPractitioners = []) => {
      const configurations = tabConfigurations[tab] || {};
      let success = false;

      if (tab === 'org') {
        if (!selectedOrganization?.org_uuid) {
          message.error('Please select an organization first');
          return false;
        }
        try {
          success = await processLLMOverrides(
            configurations,
            predefinedUseCases,
            'orgs',
            selectedOrganization.org_uuid,
            DISPLAY_NAMES.org
          );
        } catch (error) {
          message.error(`Failed to save ${DISPLAY_NAMES.org} LLM overrides`);
          return false;
        }
      } else if (tab === 'practitioner') {
        const practitionerId =
          selectedPractitioners.length > 0 ? selectedPractitioners[0] : null;
        if (!selectedOrganization?.org_uuid) {
          message.error('Please select an organization first');
          return false;
        }
        if (!practitionerId) {
          message.error('Please select a practitioner');
          return false;
        }
        try {
          success = await processLLMOverrides(
            configurations,
            predefinedUseCases,
            'practitioners',
            practitionerId,
            DISPLAY_NAMES.practitioner
          );
        } catch (error) {
          message.error(
            `Failed to save ${DISPLAY_NAMES.practitioner} LLM overrides`
          );
          return false;
        }
      } else if (tab === 'plan') {
        const planType =
          selectedPractitioners.length > 0 ? selectedPractitioners[0] : null;
        if (!selectedOrganization?.org_uuid) {
          message.error('Please select an organization first');
          return false;
        }
        if (!planType) {
          message.error('Please select a plan type');
          return false;
        }
        try {
          success = await processLLMOverrides(
            configurations,
            predefinedUseCases,
            'plan_types',
            planType,
            DISPLAY_NAMES.plan
          );
        } catch (error) {
          message.error(`Failed to save ${DISPLAY_NAMES.plan} LLM overrides`);
          return false;
        }
      }

      if (success) {
        dispatch(setTabUnsavedChangesSlice({ tab, hasChanges: false }));
      }
      return success;
    },
    [tabConfigurations, dispatch, selectedOrganization, processLLMOverrides]
  );

  return {
    selectedOrganization,
    llms,
    llmsLoading,
    llmOverrides,
    llmOverridesLoading,
    // Tab-specific functions
    initializeTabConfiguration,
    updateTabConfiguration,
    setTabSelection,
    getTabConfiguration,
    getTabUnsavedChanges,
    getTabSelection,
    resetTabConfiguration,
    saveTabConfigurations,
  };
};
